// 監控範本的純函式（畫面文字、分組、表單檢查；vitest 測 templates.test.ts）。
import { windowText } from '../model';
import type { BusinessHours, HomeNotify, Maintenance, Thresholds, TimeWindows } from '../types';
import type { Change, Names, PreviewHome, PreviewRow, TemplateBody, TemplateItem, TemplateJobSummary, TplProbe, TplRule } from './types';

type TFn = (k: string, o?: Record<string, unknown>) => string;

const NUM_KEYS = ['disk_pct', 'tls_days', 'workstation_offline_days'] as const;

/** 一列的名稱：「門檻與等級 · 交換器 / 路由器…」「探針 · https」。 */
export function rowLabel(t: TFn, item: TemplateItem, key: string, body?: TemplateBody): string {
  const it = t(`tpl.item_${item}`);
  switch (item) {
    case 'severity_overrides':
      return `${it} · ${(NUM_KEYS as readonly string[]).includes(key) ? t(`tpl.num_${key}`) : t(`threshold.rule_${key}`)}`;
    case 'notify':
      return `${it} · ${t(key === 'customer_contact' ? 'tpl.key_customer_contact' : 'tpl.key_schedule')}`;
    case 'probes': {
      const p = body?.probes?.find((x) => x.key === key);
      return `${it} · ${p?.name || key}`;
    }
    case 'auto_rules': {
      const r = body?.auto_rules?.find((x) => x.key === key);
      return `${it} · ${r?.name || r?.target_match.service || key}`;
    }
    default:
      return it;
  }
}

function dayName(t: TFn) {
  return (d: number) => t(`day.${d}`);
}

function windowsValue(t: TFn, v: TimeWindows): string {
  return (v.windows ?? []).map((w) => windowText(w, dayName(t), t('window.every_day'), t('window.all_day'))).join('；');
}

/** 一個值的一句話（預覽的「現在 → 之後」）。null / undefined = 「沒有」。 */
export function valueText(t: TFn, item: TemplateItem, key: string, v: unknown, ctx: { schedules?: Record<string, string>; users?: Record<string, string> } = {}): string {
  if (v === null || v === undefined) return item === 'notify' && key === 'customer_contact' ? t('tpl.v_owner_default') : t('tpl.v_none');
  switch (item) {
    case 'severity_overrides': {
      if (key === 'disk_pct') return `${v}%`;
      if (key === 'tls_days' || key === 'workstation_offline_days') return t('tpl.v_days', { n: v });
      const r = v as { severity: string; debounce_min: number | null };
      const d = r.debounce_min === null ? t('threshold.no_incident') : r.debounce_min === 0 ? t('threshold.immediate') : t('threshold.minutes', { n: r.debounce_min });
      return `${t(`sev.${r.severity}`)} · ${d}`;
    }
    case 'maintenance':
    case 'business_hours':
      return windowsValue(t, v as TimeWindows);
    case 'notify':
      return key === 'customer_contact' ? (ctx.users?.[v as string] ?? String(v)) : (ctx.schedules?.[v as string] ?? String(v));
    case 'probes': {
      const list = v as { target?: string }[];
      const tg = list.map((p) => p.target || t('tpl.v_collector_local'));
      return t('tpl.v_probes', { n: list.length, targets: tg.slice(0, 3).join('、') + (tg.length > 3 ? '…' : '') });
    }
    case 'auto_rules': {
      const r = v as { service: string; checks: number; targets: string[] };
      return t('tpl.v_rule', { service: r.service, checks: r.checks, n: r.targets.length });
    }
    case 'power':
      return typeof v === 'object' ? t('tpl.val_power', { warn: (v as { poe_warn_pct: number }).poe_warn_pct, high: (v as { poe_high_pct: number }).poe_high_pct }) : `${v}%`;
  }
  return String(v);
}

/** 在地修改的標籤：「晴光文具改的（劉建宏 10/21）」。 */
export function overrideText(t: TFn, ov: NonNullable<PreviewRow['local_override']>, names: Partial<Names> | undefined): string {
  const who = names?.users?.[ov.by] ?? ov.by;
  const org = names?.orgs?.[ov.by_org] ?? ov.by_org;
  const d = ov.at ? new Date(ov.at) : null;
  const date = d && !isNaN(d.getTime()) ? `${d.getMonth() + 1}/${d.getDate()}` : '';
  return t('tpl.ov_by', { org, name: who, date }).trim();
}

/** 一列在套用時的結果：蓋掉（橘）/ 保留（灰）/ 只提示（客戶欄位）/ 跳過 / 沒變。overwrite = 使用者勾了〔仍要蓋掉〕。 */
export type RowFate = 'apply' | 'overwrite_local' | 'keep' | 'hint' | 'skip' | 'same';
export function rowFate(r: PreviewRow, overwrite: boolean): RowFate {
  if (r.customer_field) return 'hint';
  if (r.skip_reason) return 'skip';
  if (r.change === 'same') return 'same';
  if (r.local_override) return r.default === 'keep' && !overwrite ? 'keep' : 'overwrite_local';
  return 'apply';
}

/** 依組織分組（組織名稱排序；同組依家戶名稱）。 */
export function groupByOrg(homes: PreviewHome[], names: Partial<Names> | undefined): { org_id: string; homes: PreviewHome[] }[] {
  const by = new Map<string, PreviewHome[]>();
  for (const h of homes) by.set(h.org_id, [...(by.get(h.org_id) ?? []), h]);
  const name = (id: string, m?: Record<string, string>) => m?.[id] ?? id;
  return [...by.entries()]
    .sort((a, b) => name(a[0], names?.orgs).localeCompare(name(b[0], names?.orgs)))
    .map(([org_id, hs]) => ({ org_id, homes: hs.sort((a, b) => name(a.home_id, names?.homes).localeCompare(name(b.home_id, names?.homes))) }));
}

export function chunk<T>(xs: T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += n) out.push(xs.slice(i, i + n));
  return out;
}

export function rowId(r: { item: string; key: string }): string {
  return `${r.item}/${r.key}`;
}

/** 這一戶的列：預設只看會變的（套用 / 保留 / 跳過 / 客戶欄位提示），沒變的收起來。 */
export function visibleRows(rows: PreviewRow[], showSame: boolean): PreviewRow[] {
  return rows.filter((r) => showSame || r.change !== 'same' || r.customer_field || !!r.skip_reason);
}

export function changeMark(c: Change): string {
  return { add: '+', update: '~', remove: '−', same: '=' }[c];
}

/** 工作是不是還在跑（要輪詢）。 */
export function jobActive(j: Pick<TemplateJobSummary, 'state'>): boolean {
  return j.state === 'queued' || j.state === 'running';
}

/** 進度：已處理 / 全部、百分比。 */
export function jobProgress(j: Pick<TemplateJobSummary, 'total' | 'counts'>): { done: number; pct: number } {
  const done = j.total - (j.counts.pending ?? 0);
  return { done, pct: j.total ? Math.round((done * 100) / j.total) : 100 };
}

// ---- 表單 ----

const KEY_RE = /^[a-z0-9][a-z0-9_-]{0,39}$/;

/** 從名稱產生 key（範本內穩定識別；英數之外的字丟掉，空的用序號）。 */
export function suggestKey(name: string, taken: string[], prefix: string): string {
  let k = name
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
  if (!KEY_RE.test(k)) k = prefix;
  let out = k;
  for (let i = 2; taken.includes(out); i++) out = `${k}-${i}`;
  return out;
}

export function newProbe(taken: string[]): TplProbe {
  return { key: suggestKey('', taken, 'probe'), match: { hosts: { class: 'server' } }, kind: 'tcp', port: 443, interval_s: 60 };
}

export function newRule(taken: string[]): TplRule {
  return { key: suggestKey('', taken, 'rule'), template: 'restart_service_on_stop', limits: { checks: 3 }, target_match: { service: '' } };
}

export function matchKind(p: TplProbe): 'hosts' | 'network_devices' | 'collector_self' {
  return p.match.network_devices ? 'network_devices' : p.match.collector_self ? 'collector_self' : 'hosts';
}

/** 送出前的檢查（同 kmgr 的 Parse；回 i18n key）。 */
export function validateBody(b: TemplateBody): string[] {
  const out = new Set<string>();
  const items = (['severity_overrides', 'maintenance', 'business_hours', 'notify', 'power'] as const).filter((k) => b[k] !== undefined).length + (b.probes?.length ? 1 : 0) + (b.auto_rules?.length ? 1 : 0);
  if (!items) out.add('tpl.err_empty');
  if (b.power) {
    const { poe_warn_pct: w, poe_high_pct: h } = b.power;
    const ok = (x: number) => Number.isInteger(x) && x >= 50 && x <= 100;
    if (!ok(w) || !ok(h) || w > h) out.add('tpl.power_err');
  }
  if (b.maintenance && !b.maintenance.windows.length) out.add('tpl.err_maint_empty');
  for (const w of [...(b.maintenance?.windows ?? []), ...(b.business_hours?.windows ?? [])]) if (!w.days.length) out.add('window.err_days');
  if (b.notify && !b.notify.schedule_id) out.add('tpl.err_schedule');
  const pk = b.probes?.map((p) => p.key) ?? [];
  if (pk.some((k) => !KEY_RE.test(k))) out.add('tpl.err_key');
  if (new Set(pk).size !== pk.length) out.add('tpl.err_key_dup');
  for (const p of b.probes ?? []) {
    if (p.kind === 'tcp' && !p.port) out.add('tpl.err_port');
    if (p.kind === 'http' && !(p.http?.path ?? '').startsWith('/')) out.add('tpl.err_http_path');
    if (p.kind === 'file' && (matchKind(p) !== 'collector_self' || !p.file?.path)) out.add('tpl.err_file');
  }
  const rk = b.auto_rules?.map((r) => r.key) ?? [];
  if (rk.some((k) => !KEY_RE.test(k))) out.add('tpl.err_key');
  if (new Set(rk).size !== rk.length) out.add('tpl.err_key_dup');
  if ((b.auto_rules ?? []).some((r) => !r.target_match.service.trim())) out.add('tpl.err_service');
  return [...out];
}

// ---- 單一家戶的〔套用到其他家戶…〕（SPEC #kinio-kms-templates「批次套用」；Claude 推論待確認） ----

/** 一個家戶目前的監控設定（讀到的才有；讀不到的那項不帶）。 */
export interface HomeSettingsSnapshot {
  thresholds?: Thresholds;
  maintenance?: Maintenance;
  business?: BusinessHours;
  notify?: HomeNotify;
}

/**
 * 家戶目前的設定 → 範本內容（走範本的建立 → 套用流程）。帶：門檻與等級（全部列 + 三個數字）、重複的維護時段、營業時段、
 * 通知的輪值表（只有範本組織自己的輪值表才帶；客戶聯絡人屬於客戶、不進範本）。不帶：探針與自動處置（對象是這個家戶自己的主機，
 * 範本要用對象類別描述，由人在編輯器加）、一次性的維護。skipped = 這個家戶有設、但沒帶進來的項目（畫面提示）。
 */
export function bodyFromHome(s: HomeSettingsSnapshot, orgScheduleIds: ReadonlySet<string>): { body: TemplateBody; skipped: TemplateItem[] } {
  const body: TemplateBody = {};
  const skipped: TemplateItem[] = [];
  if (s.thresholds) {
    body.severity_overrides = {
      rules: s.thresholds.rules.map(({ key, severity, debounce_min }) => ({ key, severity, debounce_min })),
      disk_pct: s.thresholds.disk_pct,
      tls_days: s.thresholds.tls_days,
      workstation_offline_days: s.thresholds.workstation_offline_days,
    };
  }
  const mw = s.maintenance?.schedule;
  if (mw && mw.windows.length) body.maintenance = { tz: mw.tz, windows: mw.windows.map((w) => ({ ...w, days: [...w.days] })) };
  if (s.business && s.business.windows.length) body.business_hours = { tz: s.business.tz, windows: s.business.windows.map((w) => ({ ...w, days: [...w.days] })) };
  const sid = s.notify?.schedule_id;
  if (sid) {
    if (orgScheduleIds.has(sid)) body.notify = { schedule_id: sid };
    else skipped.push('notify');
  }
  return { body, skipped };
}
